'use client';

import { useSession } from '@shared/auth';
import { type Timezone, useTimeZone } from 'next-intl';
import { createContext, type Dispatch, type ReactNode, type SetStateAction, useCallback, useContext, useMemo, useState } from 'react';
import { useLatestTransactionSummary } from '../hooks/useLatestTransactionSummary';
import { dateToMonthKey, getLast12Months } from './transactionDateHelpers';
import type { MonthKey, ScrollTarget } from './types';
import { useScrollLock } from './useScrollLock';

type TransactionsContextValue = {
  isInitialLoading: boolean;
  isEmpty: boolean;
  isFirstMonthLoaded: boolean;
  markFirstMonthLoaded: () => void;
  loadMonth: (monthKey: MonthKey) => void;
  isMonthActive: (monthKey: MonthKey) => boolean;
  lastTransactionMonthKey: MonthKey;
  acquireScrollLock: (key: MonthKey) => void;
  isScrollLocked: () => boolean;
  scrollLockVersion: number;
  monthIndexMap: Map<MonthKey, number>;
  namespace: string;
  timeZone: Timezone | undefined;
  last12Months: MonthKey[];
  currentMonthKey: MonthKey;
  scrollTarget: ScrollTarget;
  setScrollTarget: Dispatch<SetStateAction<ScrollTarget>>;
};

const TransactionsContext = createContext<TransactionsContextValue | null>(null);

function TransactionsProvider({ children, namespace }: { children: ReactNode; namespace: string }) {
  const { session } = useSession();
  const playerId = session?.profile?.id;
  const isSessionLoading = session.status === 'loading' || session.status === 'uninitialised';
  const timeZone = useTimeZone();

  const { latestTransaction, isLoading: isSummaryLoading } = useLatestTransactionSummary();

  const last12Months = useMemo(() => getLast12Months(timeZone), [timeZone]);
  const currentMonthKey: MonthKey = last12Months[0] ?? dateToMonthKey(new Date().toISOString(), timeZone);
  const lastTransactionMonthKey = latestTransaction ? dateToMonthKey(latestTransaction.date, timeZone) : currentMonthKey;

  const [enabledMonths, setEnabledMonths] = useState<Set<string>>(new Set());
  const loadMonth = useCallback((key: string) => setEnabledMonths(prev => (prev.has(key) ? prev : new Set([...prev, key]))), []);
  const isMonthActive = useCallback((key: string) => enabledMonths.has(key), [enabledMonths]);

  const { scrollLockVersion, acquireScrollLock, isScrollLocked } = useScrollLock();
  const monthIndexMap = useMemo(() => new Map(last12Months.map((month, i) => [month, i] as const)), [last12Months]);

  const [isFirstMonthLoaded, setIsFirstMonthLoaded] = useState(false);
  const markFirstMonthLoaded = useCallback(() => setIsFirstMonthLoaded(true), []);

  const [scrollTarget, setScrollTarget] = useState<ScrollTarget>(null);

  const value = useMemo<TransactionsContextValue>(
    () => ({
      isInitialLoading: isSessionLoading || isSummaryLoading,
      isEmpty: !playerId || (!isSummaryLoading && !latestTransaction),
      isFirstMonthLoaded,
      markFirstMonthLoaded,
      loadMonth,
      isMonthActive,
      lastTransactionMonthKey,
      acquireScrollLock,
      isScrollLocked,
      scrollLockVersion,
      monthIndexMap,
      namespace,
      timeZone,
      last12Months,
      currentMonthKey,
      scrollTarget,
      setScrollTarget,
    }),
    [
      isSessionLoading,
      isSummaryLoading,
      playerId,
      latestTransaction,
      isFirstMonthLoaded,
      markFirstMonthLoaded,
      loadMonth,
      isMonthActive,
      lastTransactionMonthKey,
      acquireScrollLock,
      isScrollLocked,
      scrollLockVersion,
      monthIndexMap,
      namespace,
      timeZone,
      last12Months,
      currentMonthKey,
      scrollTarget,
      setScrollTarget,
    ]
  );

  return <TransactionsContext.Provider value={value}>{children}</TransactionsContext.Provider>;
}

function useTransactionsContext(): TransactionsContextValue {
  const ctx = useContext(TransactionsContext);
  if (!ctx) throw new Error('useTransactionsContext must be used within TransactionsProvider');
  return ctx;
}

export { TransactionsProvider, useTransactionsContext };
